import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export interface HistoryEvent {
  timestamp: string
  event: string
  details: string
}

interface ShardData {
  id: string
  title: string
  status: string
  owner: string
  yaml: string
  history: HistoryEvent[]
  depends_on?: string[]
  scratchpad?: string | null
}

interface ShardInspectorModalProps {
  shard: ShardData
  onClose: () => void
  isOpen: boolean
  onMarkComplete?: (shardId: string) => void
  onShardClick?: (shardId: string) => void
  /** Returns the status of a shard by ID (for colouring dependency chips) */
  getShardStatus?: (shardId: string) => string | undefined
  /** Auth headers for API calls */
  getAuthHeaders?: () => Record<string, string>
  /** Current project name (for the prior-learning lookup) */
  projectName?: string
}

export function ShardInspectorModal({ shard, onClose, isOpen, onMarkComplete, onShardClick, getShardStatus, getAuthHeaders, projectName }: ShardInspectorModalProps) {
  // Fetch infrastructure guide for AWAITING_HUMAN_ACTION tasks
  const [guideContent, setGuideContent] = useState<string | null>(null)
  const [guideLoading, setGuideLoading] = useState(false)
  useEffect(() => {
    if (!isOpen || shard.status !== 'AWAITING_HUMAN_ACTION') {
      setGuideContent(null)
      return
    }
    setGuideLoading(true)
    // Cantor writes the guide at docs/infrastructure/<task-id>-guide.md using the
    // shard's original (upper) case; the container FS is case-sensitive, so a
    // lower-cased path 404s. Use the id verbatim.
    const guidePath = `docs/infrastructure/${shard.id}-guide.md`
    fetch(`/api/projects/file?path=${encodeURIComponent(guidePath)}`, {
      headers: getAuthHeaders?.() || {},
    })
      .then(r => r.ok ? r.text() : null)
      .then(content => setGuideContent(content))
      .catch(() => setGuideContent(null))
      .finally(() => setGuideLoading(false))
  }, [isOpen, shard.id, shard.status, getAuthHeaders])

  // Whether this task consulted prior code-review lessons and got ≥1 hit (#162, #38).
  const [priorLearningApplied, setPriorLearningApplied] = useState(false)
  useEffect(() => {
    if (!isOpen || !projectName) {
      setPriorLearningApplied(false)
      return
    }
    // Guard against a stale response winning after a rapid shard switch.
    let ignore = false
    fetch(`/api/projects/${encodeURIComponent(projectName)}/prior-lessons`, {
      headers: getAuthHeaders?.() || {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(ids => { if (!ignore) setPriorLearningApplied(Array.isArray(ids) && ids.includes(shard.id)) })
      .catch(() => { if (!ignore) setPriorLearningApplied(false) })
    return () => { ignore = true }
  }, [isOpen, projectName, shard.id, getAuthHeaders])

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase()
    if (statusLower === 'done' || statusLower === 'completed') return 'text-green-400'
    if (statusLower === 'in_progress' || statusLower === 'active') return 'text-cosmic-star'
    if (statusLower === 'awaiting_human_action') return 'text-amber-400'
    if (statusLower === 'blocked') return 'text-cosmic-plasma'
    if (statusLower === 'error') return 'text-red-400'
    return 'text-gray-400'
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="themed-panel border border-cosmic-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-cosmic-border bg-cosmic-depth">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">{shard.title}</h2>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-cosmic-nebula-light font-mono">{shard.id}</span>
                <span className="text-gray-500">•</span>
                <span className={`font-semibold ${getStatusColor(shard.status)}`}>
                  {shard.status}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-400">Owner: {shard.owner}</span>
                {priorLearningApplied && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-cosmic-nebula/15 border border-cosmic-nebula/40 text-cosmic-nebula-light"
                    title="This task consulted code-review lessons from other projects (advisory)."
                  >
                    Prior learning applied
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Human Action Required Banner */}
          {shard.status === 'AWAITING_HUMAN_ACTION' && onMarkComplete && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-amber-300 text-sm mb-3">
                This task requires you to complete an action outside of Star Forge.
                Review the guide below, then mark as complete when done.
              </p>
              <button
                onClick={() => onMarkComplete(shard.id)}
                className="w-full px-4 py-3 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors"
              >
                Mark as Complete
              </button>
            </div>
          )}

          {/* Infrastructure Guide (for AWAITING_HUMAN_ACTION tasks) */}
          {shard.status === 'AWAITING_HUMAN_ACTION' && (
            <section>
              <h3 className="text-lg font-semibold text-white mb-3">Implementation Guide</h3>
              {guideLoading ? (
                <div className="text-gray-500 text-sm py-4">Loading guide...</div>
              ) : guideContent ? (
                <div className="bg-cosmic-void border border-amber-500/20 rounded-lg p-4 overflow-x-auto text-sm markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{guideContent}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-gray-500 text-sm py-2 italic">
                  No guide found at docs/infrastructure/{shard.id.toLowerCase()}-guide.md
                </div>
              )}
            </section>
          )}

          {/* Dependencies */}
          {shard.depends_on && shard.depends_on.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-white mb-3">Dependencies</h3>
              <div className="flex flex-wrap gap-2">
                {shard.depends_on.map(dep => {
                  const depStatus = getShardStatus?.(dep)
                  const isDone = depStatus === 'DONE' || depStatus === 'APPROVED' || depStatus === 'FINAL_APPROVED'
                  return (
                  <button
                    key={dep}
                    onClick={() => onShardClick?.(dep)}
                    className={`text-sm px-3 py-1.5 rounded-lg font-mono hover:bg-white/10 transition-colors ${
                      isDone
                        ? 'bg-white/5 border border-gray-700 text-gray-500 line-through'
                        : 'bg-white/5 border border-cosmic-border text-cosmic-star hover:border-cosmic-nebula'
                    }`}
                  >
                    {dep}
                  </button>
                  )
                })}
              </div>
            </section>
          )}

          {/* YAML Section */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="text-cosmic-ether">📄</span>
              Configuration
            </h3>
            <div className="bg-cosmic-void border border-cosmic-border rounded-lg p-4 overflow-x-auto text-sm markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{shard.yaml}</ReactMarkdown>
            </div>
          </section>

          {/* Scratchpad */}
          {shard.scratchpad && (
            <section>
              <h3 className="text-lg font-semibold text-white mb-3">
                {shard.status === 'BLOCKED' ? 'Blocked Reason' : 'Scratchpad'}
              </h3>
              <div className={`bg-cosmic-void border rounded-lg p-4 text-sm whitespace-pre-wrap ${
                shard.status === 'BLOCKED' ? 'border-cosmic-plasma/30 text-cosmic-plasma/90' : 'border-cosmic-border text-gray-300'
              }`}>
                {shard.scratchpad}
              </div>
            </section>
          )}

          {/* History Timeline */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="text-cosmic-star">⏱️</span>
              History
            </h3>
            <div className="space-y-3">
              {shard.history.map((event, index) => (
                <div key={index} className="flex gap-4">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-cosmic-nebula ring-4 ring-cosmic-nebula/20" />
                    {index < shard.history.length - 1 && (
                      <div className="flex-1 w-0.5 bg-cosmic-border min-h-[2rem]" />
                    )}
                  </div>

                  {/* Event details */}
                  <div className="flex-1 pb-4">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-white font-medium capitalize">
                        {event.event.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(event.timestamp)}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{event.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
